import { useState, useRef, useCallback, useEffect } from "react";

interface UseSongAutoScrollProps {
  paneRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  lyricsRef: React.MutableRefObject<HTMLDivElement | null>;
  splitMode?: number;
  scrollOffsetsRef?: React.MutableRefObject<number[]>;
  baseScrollPosRef?: React.MutableRefObject<number>;
  unifiedYRef?: React.MutableRefObject<number>;
}

export function useSongAutoScroll({
  paneRefs,
  lyricsRef,
  splitMode = 1,
  scrollOffsetsRef: externalScrollOffsetsRef,
  baseScrollPosRef: externalBaseScrollPosRef,
  unifiedYRef: externalUnifiedYRef,
}: UseSongAutoScrollProps) {
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState<number[]>([0.15]); // Auto-scroll speed min=0.01, max=1.00, step=0.01
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);

  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const internalBaseScrollPosRef = useRef<number>(0);
  const internalScrollOffsetsRef = useRef<number[]>([0]);
  const internalUnifiedYRef = useRef<number>(0);
  const isSyncingScrollRef = useRef<boolean>(false);

  const baseScrollPosRef = externalBaseScrollPosRef || internalBaseScrollPosRef;
  const scrollOffsetsRef = externalScrollOffsetsRef || internalScrollOffsetsRef;
  const unifiedYRef = externalUnifiedYRef || internalUnifiedYRef;

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    };
  }, []);

  // Calculate instant clamp boundaries for unified scroll variable Y
  // Top boundary: y = 0 (first panel at top)
  // Bottom boundary: last active panel reaches end of song content (maxScroll - baseOffset)
  const getScrollBoundaries = useCallback(() => {
    const activeCount = Math.max(1, splitMode || 1);
    const lastIndex = activeCount - 1;
    const lastPane = paneRefs.current[lastIndex] || paneRefs.current[0] || lyricsRef.current;

    const minY = 0;
    let maxY = 0;

    if (lastPane) {
      const maxScroll = Math.max(0, lastPane.scrollHeight - lastPane.clientHeight);
      const lastBaseOffset = scrollOffsetsRef.current[lastIndex] || 0;
      maxY = Math.max(0, maxScroll - lastBaseOffset);
    }

    return { minY, maxY, lastIndex, activeCount };
  }, [splitMode, paneRefs, lyricsRef, scrollOffsetsRef]);

  // Apply delta to unified Y scroll variable across all active panels with instant boundary clamping
  const applyUnifiedScrollDelta = useCallback(
    (deltaY: number): boolean => {
      const { minY, maxY, activeCount } = getScrollBoundaries();
      const currentY = unifiedYRef.current;
      const targetY = currentY + deltaY;
      const clampedY = Math.max(minY, Math.min(maxY, targetY));

      // Boundary reached: instant stop with no overshoot
      if (clampedY === currentY) {
        return false;
      }

      unifiedYRef.current = clampedY;
      baseScrollPosRef.current = (scrollOffsetsRef.current[0] || 0) + clampedY;

      isSyncingScrollRef.current = true;
      for (let i = 0; i < activeCount; i++) {
        const pane = paneRefs.current[i];
        if (pane) {
          const paneBase = scrollOffsetsRef.current[i] || 0;
          pane.scrollTop = paneBase + clampedY;
        }
      }

      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });

      return true;
    },
    [getScrollBoundaries, paneRefs, scrollOffsetsRef, baseScrollPosRef, unifiedYRef]
  );

  const toggleScroll = useCallback(() => {
    if (isScrolling) {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      setIsScrolling(false);
    } else {
      const speed = scrollSpeed[0];

      // 25ms tick (40 fps) with subpixel precision
      // Moves all panels based on unified Y variable and instant clamping at boundary
      scrollIntervalRef.current = setInterval(() => {
        const delta = speed * 0.8;
        applyUnifiedScrollDelta(delta);
      }, 25);
      setIsScrolling(true);
    }
  }, [isScrolling, scrollSpeed, applyUnifiedScrollDelta]);

  const handleSpeedChange = useCallback(
    (_e: Event | null, newVal: number | number[]) => {
      const val = Array.isArray(newVal) ? newVal : [newVal];
      const clampedVal = [Math.max(0.01, Math.min(1.0, parseFloat(val[0].toFixed(2))))];
      setScrollSpeed(clampedVal);
      if (isScrolling) {
        if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = setInterval(() => {
          const delta = clampedVal[0] * 0.8;
          applyUnifiedScrollDelta(delta);
        }, 25);
      }
    },
    [isScrolling, applyUnifiedScrollDelta]
  );

  // Synchronized Manual Scrolling: Scrolling any pane moves all panels synchronously based on unified Y
  const handlePaneScroll = useCallback(
    (scrolledIndex: number) => {
      if (isSyncingScrollRef.current) return;
      const sourcePane = paneRefs.current[scrolledIndex];
      if (!sourcePane) return;

      isSyncingScrollRef.current = true;
      const currentScrollTop = sourcePane.scrollTop;
      const baseOffset = scrollOffsetsRef.current[scrolledIndex] || 0;
      const rawDelta = currentScrollTop - baseOffset;

      const { minY, maxY, activeCount } = getScrollBoundaries();
      const clampedY = Math.max(minY, Math.min(maxY, rawDelta));

      unifiedYRef.current = clampedY;
      baseScrollPosRef.current = (scrollOffsetsRef.current[0] || 0) + clampedY;

      // Instant boundary clamping on source pane if it exceeded boundaries
      const expectedSourceTop = baseOffset + clampedY;
      if (Math.abs(sourcePane.scrollTop - expectedSourceTop) > 0.5) {
        sourcePane.scrollTop = expectedSourceTop;
      }

      // Sync all other active panes
      for (let idx = 0; idx < activeCount; idx++) {
        if (idx === scrolledIndex) continue;
        const pane = paneRefs.current[idx];
        if (pane) {
          const paneBaseOffset = scrollOffsetsRef.current[idx] || 0;
          const targetPos = paneBaseOffset + clampedY;
          if (Math.abs(pane.scrollTop - targetPos) > 0.5) {
            pane.scrollTop = targetPos;
          }
        }
      }

      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    },
    [getScrollBoundaries, paneRefs, scrollOffsetsRef, baseScrollPosRef, unifiedYRef]
  );

  return {
    isScrolling,
    scrollSpeed,
    setScrollSpeed,
    mobileSettingsOpen,
    setMobileSettingsOpen,
    toggleScroll,
    handleSpeedChange,
    handlePaneScroll,
    applyUnifiedScrollDelta,
    getScrollBoundaries,
    unifiedYRef,
    baseScrollPosRef,
    scrollOffsetsRef,
    isSyncingScrollRef,
  };
}
